import { useContext, useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import AddCardOutlinedIcon from "@mui/icons-material/AddCardOutlined";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CustomButton, NoAccess } from "src/design";
import { CircularLoader } from "src/design/CircularLoader";
import { PaymentType } from "src/business/payment/payment";
import { upgradeableTiers } from "src/business/payment/upgradeTarget";
import {
  isBillingInfoVisible,
  isPaymentSurfaceEnabled,
} from "src/helpers/FeatureCapabilityConfig";
import {
  useCurrentPayment,
  usePaymentHistory,
} from "src/data/payment/queries/payment";
import { useSubscriptionTier } from "src/data/subscription/subscriptionTier";
import { useGetSubscriptionTiers } from "src/data/landing/queries/subscriptionTiers";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";
import {
  colorTextError,
  colorTextMuted,
  colorTextPrimary,
} from "src/design/tokens";
import { BankTransferCard } from "./components/BankTransferCard";
import { DeclarePaymentDialog } from "./components/DeclarePaymentDialog";
import { PaymentHistoryTable } from "./components/PaymentHistoryTable";
import { PendingPaymentCard } from "./components/PendingPaymentCard";

/**
 * The restaurateur's payment surface: what is under review, how to pay, and
 * everything paid so far.
 *
 * It gates itself on organization ownership plus the organization read
 * permission instead of sitting behind a capability boundary — a restaurant
 * whose subscription has lapsed and holds no capabilities is exactly the one
 * that needs to reach this page.
 */
const Payments = () => {
  const { t } = useTranslation();
  const { user, isLoading: userLoading } = useContext(UserContext);
  const { restaurants, unsubscribedRestaurants } =
    useContext(RestaurantContext);
  const { restaurantId = "" } = useParams<{ restaurantId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [declareOpen, setDeclareOpen] = useState(false);
  const [declareType, setDeclareType] = useState<PaymentType>("RENEWAL");
  const [presetTier, setPresetTier] = useState<string | null>(null);

  const authorized =
    isPaymentSurfaceEnabled(user) && isBillingInfoVisible(user);

  const currentPayment = useCurrentPayment(authorized ? restaurantId : "");
  const history = usePaymentHistory(authorized ? restaurantId : "");
  const { data: subscription } = useSubscriptionTier(
    authorized ? restaurantId : "",
  );
  const { data: catalog } = useGetSubscriptionTiers();

  // The entry points carry their intent in the URL rather than in router state,
  // so the link survives a reload and can be forwarded to whoever holds the
  // bank credentials. It is consumed once and then cleared.
  const declareParam = searchParams.get("declare");
  const tierParam = searchParams.get("tier");

  useEffect(() => {
    if (!declareParam) return;

    setDeclareType(declareParam === "upgrade" ? "UPGRADE" : "RENEWAL");
    setPresetTier(tierParam);
    setDeclareOpen(true);
    setSearchParams({}, { replace: true });
  }, [declareParam, tierParam, setSearchParams]);

  if (userLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularLoader />
      </Box>
    );
  }

  if (!authorized) return <NoAccess />;

  // The subscribed list drops a restaurant whose subscription lapsed, and that
  // restaurant is exactly who this page is for: fall back to what the
  // organization still knows about it so the transfer reference keeps its name.
  const restaurantName =
    restaurants.find((entry) => entry.id === restaurantId)?.name ??
    unsubscribedRestaurants.find((entry) => entry.id === restaurantId)?.name ??
    "";
  const currentTier = subscription?.tier ?? null;
  const pending = currentPayment.data ?? null;
  const canUpgrade = upgradeableTiers(catalog, currentTier).length > 0;
  const isLoading = currentPayment.isLoading || history.isLoading;

  const openDeclaration = (type: PaymentType) => {
    setDeclareType(type);
    setPresetTier(null);
    setDeclareOpen(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        maxWidth: 1100,
        mx: "auto",
      }}
    >
      <Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: colorTextPrimary }}
        >
          {t("payments.pageTitle")}
        </Typography>
        <Typography variant="body1" sx={{ color: colorTextMuted }}>
          {t("payments.pageSubtitle")}
        </Typography>
      </Box>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularLoader />
        </Box>
      )}

      {!isLoading && history.isError && (
        <Typography variant="body2" sx={{ color: colorTextError }}>
          {t("payments.loadError")}
        </Typography>
      )}

      {!isLoading && !history.isError && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {pending ? (
              <PendingPaymentCard
                payment={pending}
                restaurantId={restaurantId}
                onConflict={() => currentPayment.refetch()}
              />
            ) : (
              // Declaring is offered only when nothing is awaiting review: the
              // backend refuses a second pending payment with a 409.
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <CustomButton
                  color="black"
                  startIcon={<AddCardOutlinedIcon fontSize="small" />}
                  onClick={() => openDeclaration("RENEWAL")}
                >
                  {t("payments.declare.renewAction")}
                </CustomButton>
                {canUpgrade && (
                  <CustomButton
                    color="inverted"
                    onClick={() => openDeclaration("UPGRADE")}
                  >
                    {t("payments.declare.upgradeAction")}
                  </CustomButton>
                )}
              </Box>
            )}
          </Grid>

          {/* Both full width: the history table has seven columns and a
              minimum width, and in a 7/12 column it scrolled its proof
              download out of sight at desktop widths. */}
          <Grid item xs={12}>
            <BankTransferCard
              restaurantName={restaurantName}
              restaurantId={restaurantId}
            />
          </Grid>

          <Grid item xs={12}>
            <PaymentHistoryTable
              payments={history.data ?? []}
              restaurantId={restaurantId}
            />
          </Grid>
        </Grid>
      )}

      <DeclarePaymentDialog
        open={declareOpen}
        onClose={() => setDeclareOpen(false)}
        restaurantId={restaurantId}
        restaurantName={restaurantName}
        currentTier={currentTier}
        catalog={catalog}
        initialType={declareType}
        presetTier={presetTier}
        onConflict={() => currentPayment.refetch()}
      />
    </Box>
  );
};

export default Payments;
